/**
 * Browser Detection Core Logic
 * Pure JavaScript - single source of truth for browser detection
 * 
 * Used by:
 * - browser-hard-block.js
 * - browser-detection.service.ts
 */

const THRESHOLDS = {
  chrome: { name: 'Chrome', minSupported: 94, minRecommended: 122 },
  edge: { name: 'Edge', minSupported: 94, minRecommended: 122 },
  firefox: { name: 'Firefox', minSupported: 93, minRecommended: 122 },
  safari: { name: 'Safari', minSupported: 16.4, minRecommended: 17.4 }
};

/**
 * Detect and validate browser
 * @returns {Object|null} { browser, name, version, isSupported, isRecommended, recommendedVersion } or null
 */
function detectBrowser() {
  const ua = navigator.userAgent;
  let browser = null;
  let version = 0;
  
  // IE 11
  if (/MSIE|Trident/.test(ua)) {
    return { browser: 'ie', name: 'Internet Explorer', version: 11, isSupported: false, isRecommended: false, recommendedVersion: null };
  }
  
  // Chrome (exclude Edge, Samsung, Opera)
  if (/Chrome\/([0-9]+)/.test(ua) && !/Edg/.test(ua) && !/SamsungBrowser/.test(ua) && !/OPR/.test(ua)) {
    browser = 'chrome';
    version = parseInt(RegExp.$1);
  }
  // Edge
  else if (/Edg\/([0-9]+)/.test(ua)) {
    browser = 'edge';
    version = parseInt(RegExp.$1);
  }
  // Firefox
  else if (/Firefox\/([0-9]+)/.test(ua)) {
    browser = 'firefox';
    version = parseInt(RegExp.$1);
  }
  // Safari
  else if (/Version\/([0-9]+\.[0-9]+).*Safari/.test(ua)) {
    browser = 'safari';
    version = parseFloat(RegExp.$1);
  }
  
  if (!browser) return null;
  
  const threshold = THRESHOLDS[browser];
  
  return {
    browser: browser,
    name: threshold.name,
    version: version,
    isSupported: version >= threshold.minSupported,
    isRecommended: version >= threshold.minRecommended,
    recommendedVersion: threshold.minRecommended
  };
}

/**
 * Get recommended browsers list as string separated values
 * @returns {string}
 */
function getRecommendedList() {
  return Object.values(THRESHOLDS)
    .map(b => `${b.name} ${b.minRecommended}+`)
    .join(', ');
}

/**
 * Get minimum supported versions list
 * @returns {Array} [{ name, version }]
 */
function getMinimumVersions() {
  return Object.values(THRESHOLDS).map(b => ({
    name: b.name,
    version: b.minSupported
  }));
}

// ES Module exports (for esbuild bundling and TypeScript imports)
export { detectBrowser, getRecommendedList, getMinimumVersions, THRESHOLDS };
